<template>
  <div v-if="count" :aria-busy="loading" class="app-pagination" :class="`app-pagination--${placement}`">
    <nav v-if="totalPages > 1" class="app-pagination__controls" :aria-label="label">
      <button v-if="edges" class="app-pagination__arrow app-pagination__arrow--first" type="button" :disabled="loading || page <= 1" aria-label="First page" @click="onChange(1)" />
      <button class="app-pagination__arrow" type="button" :disabled="loading || page <= 1" aria-label="Previous page" @click="onChange(page - 1)" />
      <button
        v-for="pageNumber in visiblePages"
        :key="pageNumber"
        :disabled="loading"
        type="button"
        :class="{ 'app-pagination__page--active': pageNumber === page }"
        :aria-current="pageNumber === page ? 'page' : null"
        @click="onChange(pageNumber)"
      >
        {{ pageNumber }}
      </button>
      <button class="app-pagination__arrow app-pagination__arrow--next" type="button" :disabled="loading || page >= totalPages" aria-label="Next page" @click="onChange(page + 1)" />
      <button v-if="edges" class="app-pagination__arrow app-pagination__arrow--last" type="button" :disabled="loading || page >= totalPages" aria-label="Last page" @click="onChange(totalPages)" />
    </nav>
    <p class="app-pagination__records">Showing {{ firstRecord }}–{{ lastRecord }} of {{ count }} records</p>
  </div>
</template>

<script>
export default {
  name: 'AppPagination',
  props: {
    design: { type: String, required: true, validator: value => value === 'new' },
    placement: { type: String, default: 'footer', validator: value => ['footer', 'inline', 'embedded'].includes(value) },
    loading: { type: Boolean, default: false },
    label: { type: String, default: 'Record pages' },
    // Opt-in buttons that jump to the first and the last page, for lists with many pages.
    edges: { type: Boolean, default: false },
    page: { type: Number, required: true },
    pages: { type: Number, default: null },
    count: { type: Number, required: true },
    visibleCount: { type: Number, required: true },
    pageSize: { type: Number, required: true }
  },
  computed: {
    totalPages() { return this.pages || Math.max(1, Math.ceil(this.count / this.pageSize)) },
    firstRecord() {
      return this.visibleCount ? ((this.page - 1) * this.pageSize) + 1 : 0
    },
    lastRecord() {
      return this.visibleCount ? Math.min(this.count, ((this.page - 1) * this.pageSize) + this.visibleCount) : 0
    },
    visiblePages() {
      const first = Math.max(1, Math.min(this.page - 2, this.totalPages - 4))
      const last = Math.min(this.totalPages, first + 4)
      return Array.from({ length: last - first + 1 }, (_, index) => first + index)
    }
  },
  methods: {
    onChange(page) {
      if (this.loading || page < 1 || page > this.totalPages || page === this.page) return
      this.$emit('change', page)
    }
  }
}
</script>

<style lang="scss" scoped>
.app-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: $ui-space-16;
  min-width: 0;
  font-family: $ui-font-body;

  &--footer { padding: $ui-space-16 $ui-space-20; border-top: 1px solid $ui-line; }
  &--inline { margin-top: $ui-space-20; }
}

.app-pagination__controls {
  display: flex;
  align-items: center;
  gap: $ui-space-4;
}

.app-pagination__controls button {
  display: inline-flex;
  min-width: $ui-control-height;
  height: $ui-control-height;
  align-items: center;
  justify-content: center;
  border: 1px solid $ui-line-strong;
  border-radius: $ui-control-radius;
  background-color: $ui-surface;
  color: $ui-text;
  cursor: pointer;
  flex: 0 0 auto;
  padding: 0 $ui-space-8;
  font-family: $ui-font-body;
  font-size: $ui-font-size-control;
  font-weight: 500;
}

.app-pagination__arrow::before {
  content: '';
  width: 6px;
  height: 10px;
  background: url('~@/assets/img/arrow-left-icon.svg') center / contain no-repeat;
}

.app-pagination__arrow--next::before,
.app-pagination__arrow--last::before {
  transform: rotate(180deg);
}

// First / last page: two of the same 6×10 arrows side by side.
.app-pagination__arrow--first::before,
.app-pagination__arrow--last::before {
  width: 14px;
  background:
    url('~@/assets/img/arrow-left-icon.svg') left center / 6px 10px no-repeat,
    url('~@/assets/img/arrow-left-icon.svg') right center / 6px 10px no-repeat;
}

.app-pagination__controls .app-pagination__page--active {
  border-color: var(--ui-accent, #{$ui-blue});
  background-color: var(--ui-accent-soft, #{$ui-blue-soft});
  color: var(--ui-accent, #{$ui-blue});
}

.app-pagination__controls button:hover:not(:disabled) {
  background-color: var(--ui-accent-soft, #{$ui-blue-soft});
}

.app-pagination__controls button:focus-visible {
  outline: 3px solid var(--ui-accent-soft, #{rgba($ui-blue, .18)});
  outline-offset: 2px;
}

.app-pagination__controls button:disabled {
  cursor: not-allowed;
  opacity: .45;
}

.app-pagination__records {
  margin: 0 0 0 auto;
  color: $ui-muted;
  font-size: $ui-font-size-control;
  font-weight: 500;
}

@media (max-width: 600px) {
  .app-pagination { align-items: stretch; flex-direction: column; }
  .app-pagination__controls { max-width: 100%; overflow-x: auto; padding: $ui-space-4; margin: -$ui-space-4; }
  .app-pagination__records { margin-left: 0; }
}
</style>
